// Pure helpers for SearchableSelect (PRODOPSAI-521).
//
// Module-level so SonarCloud rules S7721 (helpers inside render) + S3776
// (cognitive complexity > 15) cannot fire on the component file.

export interface SearchableOption {
  value: string;
  label: string;
  sublabel?: string | null;
}

export interface SearchableGroup {
  label: string;
  options: SearchableOption[];
}

export interface FlatOptionRow {
  /** Option to render (groupHeader === null) or group header (option === null). */
  option: SearchableOption | null;
  groupHeader: string | null;
}

/**
 * Case-insensitive substring match against `label` AND `sublabel`.
 * Empty query matches everything.
 */
export function matchesQuery(option: SearchableOption, query: string): boolean {
  const trimmed = query.trim().toLowerCase();
  if (trimmed === '') return true;
  if (option.label.toLowerCase().includes(trimmed) === true) return true;
  const sub = option.sublabel;
  if (sub !== null && sub !== undefined && sub !== '') {
    if (sub.toLowerCase().includes(trimmed) === true) return true;
  }
  return false;
}

/**
 * Filter a flat options list against the query, preserving order.
 */
export function filterOptions(
  options: readonly SearchableOption[],
  query: string,
): SearchableOption[] {
  return options.filter((o) => matchesQuery(o, query));
}

/**
 * Filter grouped options, dropping empty groups after filtering.
 */
export function filterGroups(
  groups: readonly SearchableGroup[],
  query: string,
): SearchableGroup[] {
  return groups
    .map((g) => ({ label: g.label, options: filterOptions(g.options, query) }))
    .filter((g) => g.options.length > 0);
}

/**
 * Flatten grouped (or ungrouped) options into a single render list with
 * interleaved group-header rows. The flat list drives keyboard navigation —
 * only `option !== null` rows are focusable.
 */
export function flattenForRender(
  options: readonly SearchableOption[],
  groups: readonly SearchableGroup[] | undefined,
  query: string,
): FlatOptionRow[] {
  if (groups !== undefined && groups.length > 0) {
    const filtered = filterGroups(groups, query);
    const rows: FlatOptionRow[] = [];
    for (const g of filtered) {
      rows.push({ option: null, groupHeader: g.label });
      for (const opt of g.options) {
        rows.push({ option: opt, groupHeader: null });
      }
    }
    return rows;
  }
  return filterOptions(options, query).map((opt) => ({
    option: opt,
    groupHeader: null,
  }));
}

/**
 * Index of selectable (option, not header) rows in the flat list.
 */
export function selectableIndices(rows: readonly FlatOptionRow[]): number[] {
  const out: number[] = [];
  for (let i = 0; i < rows.length; i += 1) {
    if (rows[i].option !== null) out.push(i);
  }
  return out;
}

/**
 * Compute the next highlight index for ↑/↓ navigation. Skips group-header rows.
 */
export function nextHighlightIndex(
  rows: readonly FlatOptionRow[],
  current: number,
  direction: 1 | -1,
): number {
  const indices = selectableIndices(rows);
  if (indices.length === 0) return -1;
  const currentSpot = indices.indexOf(current);
  if (currentSpot === -1) {
    return direction === 1 ? indices[0] : indices.at(-1)!;
  }
  const nextSpot = (currentSpot + direction + indices.length) % indices.length;
  return indices[nextSpot];
}

/**
 * Find the option matching a controlled `value` (or null if not present).
 */
export function findOptionByValue(
  options: readonly SearchableOption[],
  groups: readonly SearchableGroup[] | undefined,
  value: string,
): SearchableOption | null {
  if (value === '') return null;
  for (const opt of options) {
    if (opt.value === value) return opt;
  }
  if (groups !== undefined) {
    for (const g of groups) {
      for (const opt of g.options) {
        if (opt.value === value) return opt;
      }
    }
  }
  return null;
}
